import { memo, useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Map, Marker, Popup, MapRef } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTranslate } from "helpers";
import { ContactAddressMapTabProps } from "./types";
import { NoResults } from "ui/components/NoResults";
import { ContactAddressRow } from "services/api/contacts/types";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const ContactAddressMapTab: React.FC<ContactAddressMapTabProps> = (props) => {
  const { searchResults, isLoading, onRowClick, onDeleteClick, onGoogleMapClick, className } = props;

  const { t } = useTranslate();
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [selectedAddress, setSelectedAddress] = useState<ContactAddressRow | null>(null);
  const [viewState, setViewState] = useState({
    latitude: 45.815,
    longitude: 15.9819,
    zoom: 10,
  });

  // ResizeObserver za dinamičko resize karte
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Kada se container promijeni, reci mapi da se resiza
      mapRef.current?.resize();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const addressesWithCoords = useMemo(() => {
    return searchResults.data.filter(
      (addr) => addr.latitude !== null && addr.longitude !== null
    );
  }, [searchResults.data]);

  const initialViewState = useMemo(() => {
    if (addressesWithCoords.length > 0) {
      const firstAddr = addressesWithCoords[0];
      return {
        latitude: firstAddr.latitude!,
        longitude: firstAddr.longitude!,
        zoom: addressesWithCoords.length === 1 ? 14 : 10,
      };
    }
    return viewState;
  }, [addressesWithCoords]);

  const handleMarkerClick = useCallback(
    (e: React.MouseEvent, address: ContactAddressRow) => {
      e.stopPropagation();
      setSelectedAddress(address);
    },
    []
  );

  const formatAddress = (address: ContactAddressRow) => {
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.postalCode,
      address.state,
      address.country,
    ].filter(Boolean);
    return parts.join(", ");
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-full w-full min-h-[300px] ${className ?? ""}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (searchResults.pagination.totalPages === 0) {
    return <NoResults message={t("admin.contact.address.search.noresults")} />;
  }

  if (addressesWithCoords.length === 0) {
    return (
      <NoResults message={t("admin.contact.address.map.nocoordinates")} />
    );
  }

  return (
    <div ref={containerRef} className={`relative h-full w-full min-h-[300px] ${className ?? ""}`}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapLib={mapboxgl}
        initialViewState={initialViewState}
        onMove={(evt) => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        style={{ width: "100%", height: "100%" }}
      >
        {addressesWithCoords.map((address) => (
          <Marker
            key={address.id}
            latitude={address.latitude!}
            longitude={address.longitude!}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              setSelectedAddress(address);
            }}
          >
            <div
              className={`text-2xl cursor-pointer transition-transform hover:scale-125 ${
                address.primary ? "text-green-600" : "text-red-600"
              }`}
              onClick={(e) => handleMarkerClick(e, address)}
            >
              📍
            </div>
          </Marker>
        ))}

        {selectedAddress && (
          <Popup
            latitude={selectedAddress.latitude!}
            longitude={selectedAddress.longitude!}
            onClose={() => setSelectedAddress(null)}
            closeButton={true}
            closeOnClick={false}
            anchor="bottom"
            offset={25}
          >
            <div className="p-2 min-w-[200px]">
              <div className="flex items-center justify-between mb-2">
                {selectedAddress.primary && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {t("admin.contact.address.primary")}
                  </span>
                )}
              </div>
              <p className="text-sm font-medium mb-1">
                {formatAddress(selectedAddress)}
              </p>
              {selectedAddress.description && (
                <p className="text-xs text-gray-500 mb-2">
                  {selectedAddress.description}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => onRowClick(selectedAddress.id)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {t("admin.edit")}
                </button>
                <button
                  onClick={(e) =>
                    onGoogleMapClick(
                      e as unknown as React.MouseEvent<HTMLDivElement>,
                      selectedAddress.latitude,
                      selectedAddress.longitude
                    )
                  }
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Google Maps
                </button>
                <button
                  onClick={(e) =>
                    onDeleteClick(
                      e as unknown as React.MouseEvent<HTMLDivElement>,
                      selectedAddress.id
                    )
                  }
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  {t("admin.delete")}
                </button>
              </div>
            </div>
          </Popup>
        )}
      </Map>

      {/* Legend */}
      <div className="absolute top-4 right-4 bg-white rounded-lg p-2 shadow-lg text-xs">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-green-600">📍</span>
          <span>{t("admin.contact.address.primary")}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-red-600">📍</span>
          <span>{t("admin.contact.address.secondary")}</span>
        </div>
      </div>
    </div>
  );
};

export default memo(ContactAddressMapTab);
